import { useState, useEffect, useCallback } from "react";
import { useEditorStore } from "../../../stores/editorStore";
import { blockRenderers } from "../blocks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors, typography } from "../../../styles/tokens";
import { inputStyle, labelStyle, smallInput, tinyBtn, rowStyle, btnStyle, splitLayout, editorSide, previewSide } from "./shared";

const SEV_OPTIONS = [
  { value: "critical", label: "Critique" },
  { value: "high", label: "Élevé" },
  { value: "medium", label: "Moyen" },
  { value: "low", label: "Faible" },
];

export function VulnerabilityCardEditor({ block }) {
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const accentColor = useEditorStore((s) => s.report?.accent_color) || colors.accentBright;
  const [data, setData] = useState(() => ({ ...block.data }));

  const update = useCallback((patch) => {
    setData((prev) => {
      const next = { ...prev, ...patch };
      return next;
    });
  }, []);

  useEffect(() => { updateBlock(block.id, data); }, [data, block.id, updateBlock]);

  const refs = data.references || [];
  const tags = data.tags || [];

  const Renderer = blockRenderers["vulnerability-card"];
  const previewBlock = { ...block, data };

  return (
    <div style={splitLayout}>
      <div style={editorSide}>
        <div>
          <div style={labelStyle}>Titre</div>
          <input value={data.title || ""} onChange={(e) => update({ title: e.target.value })} style={smallInput} />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Sévérité</div>
            <select value={data.severity || "medium"} onChange={(e) => update({ severity: e.target.value })} style={{ ...smallInput, cursor: "pointer" }}>
              {SEV_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Score CVSS</div>
            <input type="number" min={0} max={10} step={0.1} value={data.cvss ?? ""} onChange={(e) => update({ cvss: e.target.value === "" ? null : Number(e.target.value) })} style={smallInput} />
          </div>
        </div>

        <div>
          <div style={labelStyle}>Vecteur CVSS</div>
          <input value={data.vector || ""} onChange={(e) => update({ vector: e.target.value })} placeholder="CVSS:3.1/AV:N/AC:L/..." style={smallInput} />
        </div>

        <div>
          <div style={labelStyle}>Description</div>
          <textarea value={data.description || ""} onChange={(e) => update({ description: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
        </div>

        <div>
          <div style={labelStyle}>Remédiation</div>
          <textarea value={data.remediation || ""} onChange={(e) => update({ remediation: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
        </div>

        {/* References */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={labelStyle}>Références ({refs.length})</div>
            <button onClick={() => update({ references: [...refs, { title: "", url: "" }] })} style={{ ...tinyBtn, width: "auto", padding: "3px 8px", color: colors.accentBright, borderColor: colors.accentBright }}>
              <FontAwesomeIcon icon="plus" style={{ fontSize: 9 }} />
            </button>
          </div>
          {refs.map((ref, i) => (
            <div key={i} style={{ display: "flex", gap: 4, marginTop: 4 }}>
              <input value={ref.title || ""} onChange={(e) => { const r = [...refs]; r[i] = { ...r[i], title: e.target.value }; update({ references: r }); }} placeholder="Titre" style={{ ...smallInput, flex: 1 }} />
              <input value={ref.url || ""} onChange={(e) => { const r = [...refs]; r[i] = { ...r[i], url: e.target.value }; update({ references: r }); }} placeholder="URL" style={{ ...smallInput, flex: 2 }} />
              <button onClick={() => update({ references: refs.filter((_, j) => j !== i) })} style={tinyBtn}>
                <FontAwesomeIcon icon="times" style={{ fontSize: 9, color: colors.error }} />
              </button>
            </div>
          ))}
        </div>

        {/* Tags */}
        <div>
          <div style={labelStyle}>Tags (séparés par virgule)</div>
          <input value={tags.join(", ")} onChange={(e) => update({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} placeholder="OWASP A03, CWE-89" style={smallInput} />
        </div>
      </div>

      <div style={previewSide}>
        <div style={labelStyle}>Aperçu</div>
        {Renderer && <Renderer block={previewBlock} accentColor={accentColor} />}
      </div>
    </div>
  );
}
